export type Category = {
  id: string;
  name: string;
  nameAr: string;
  icon: string;
};

export type MenuItem = {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  price: number;
  image: string;
  category: string;
  tags: string[];
  popular?: boolean;
  sizes?: { label: string; priceAdd: number }[];
  extras?: { name: string; nameAr: string; price: number }[];
  removable?: string[];
};

export type Promotion = {
  id: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  code: string;
  discount: number;
  image: string;
};

export type Order = {
  id: string;
  number: string;
  type: "dine-in" | "takeaway" | "delivery";
  items: { menuItemId: string; name: string; quantity: number; price: number }[];
  total: number;
  status: "new" | "preparing" | "ready" | "out-for-delivery" | "completed" | "cancelled";
  date: string;
  paymentMethod: string;
};

export const categories: Category[] = [
  { id: "burgers", name: "Burgers", nameAr: "برغر", icon: "🍔" },
  { id: "chicken", name: "Chicken", nameAr: "دجاج", icon: "🍗" },
  { id: "sides", name: "Sides", nameAr: "أطباق جانبية", icon: "🍟" },
  { id: "drinks", name: "Drinks", nameAr: "مشروبات", icon: "🥤" },
  { id: "desserts", name: "Desserts", nameAr: "حلويات", icon: "🍩" },
  { id: "combos", name: "Combo Meals", nameAr: "وجبات كومبو", icon: "🎁" },
];

export const menuItems: MenuItem[] = [
  {
    id: "1",
    name: "Classic Smash Burger",
    nameAr: "برغر سماش كلاسيك",
    description: "Double beef patty, American cheese, pickles, special sauce",
    descriptionAr: "طبقتان من اللحم البقري، جبنة أمريكية، مخلل، صوص خاص",
    price: 8.99,
    image: "https://placehold.co/400x300/FF6B35/fff?text=Smash+Burger",
    category: "burgers",
    tags: ["halal"],
    popular: true,
    sizes: [
      { label: "Single", priceAdd: 0 },
      { label: "Double", priceAdd: 2 },
      { label: "Triple", priceAdd: 4 },
    ],
    extras: [
      { name: "Extra Cheese", nameAr: "جبنة إضافية", price: 1 },
      { name: "Bacon", nameAr: "لحم مقدد", price: 1.5 },
      { name: "Jalapeños", nameAr: "فلفل هالابينو", price: 0.75 },
    ],
    removable: ["Pickles", "Sauce", "Onions"],
  },
  {
    id: "2",
    name: "Spicy Chicken Sandwich",
    nameAr: "ساندويتش دجاج حار",
    description: "Crispy chicken, spicy mayo, jalapeños, lettuce",
    descriptionAr: "دجاج مقرمش، مايونيز حار، فلفل هالابينو، خس",
    price: 9.49,
    image: "https://placehold.co/400x300/E63946/fff?text=Spicy+Chicken",
    category: "chicken",
    tags: ["spicy", "halal"],
    popular: true,
    sizes: [
      { label: "Regular", priceAdd: 0 },
      { label: "Large", priceAdd: 1.5 },
    ],
    extras: [
      { name: "Extra Sauce", nameAr: "صوص إضافي", price: 0.5 },
      { name: "Cheese Slice", nameAr: "شريحة جبنة", price: 1 },
    ],
    removable: ["Jalapeños", "Lettuce", "Mayo"],
  },
  {
    id: "3",
    name: "Loaded Fries",
    nameAr: "بطاطس محملة",
    description: "Cheese sauce, bacon bits, green onion",
    descriptionAr: "صوص جبنة، قطع لحم مقدد، بصل أخضر",
    price: 5.99,
    image: "https://placehold.co/400x300/F4A261/fff?text=Loaded+Fries",
    category: "sides",
    tags: [],
    popular: true,
    sizes: [
      { label: "Regular", priceAdd: 0 },
      { label: "Large", priceAdd: 2 },
    ],
    extras: [
      { name: "Extra Cheese", nameAr: "جبنة إضافية", price: 1 },
      { name: "Sour Cream", nameAr: "كريمة حامضة", price: 0.75 },
    ],
    removable: ["Bacon", "Green Onion"],
  },
  {
    id: "4",
    name: "Chocolate Milkshake",
    nameAr: "ميلك شيك شوكولاتة",
    description: "Thick, creamy, hand-spun milkshake",
    descriptionAr: "ميلك شيك سميك وكريمي محضّر يدوياً",
    price: 4.99,
    image: "https://placehold.co/400x300/6B4226/fff?text=Milkshake",
    category: "drinks",
    tags: [],
    popular: false,
    sizes: [
      { label: "Medium", priceAdd: 0 },
      { label: "Large", priceAdd: 1.5 },
    ],
    extras: [
      { name: "Whipped Cream", nameAr: "كريمة مخفوقة", price: 0.5 },
      { name: "Extra Chocolate", nameAr: "شوكولاتة إضافية", price: 0.75 },
    ],
    removable: [],
  },
  {
    id: "5",
    name: "Churros (6pc)",
    nameAr: "تشوروز (6 قطع)",
    description: "Cinnamon sugar with chocolate dipping sauce",
    descriptionAr: "قرفة وسكر مع صوص شوكولاتة للتغميس",
    price: 3.99,
    image: "https://placehold.co/400x300/D4A373/fff?text=Churros",
    category: "desserts",
    tags: ["vegan"],
    popular: false,
    extras: [
      { name: "Extra Dipping Sauce", nameAr: "صوص تغميس إضافي", price: 0.75 },
      { name: "Caramel Sauce", nameAr: "صوص كراميل", price: 0.75 },
    ],
    removable: [],
  },
  {
    id: "6",
    name: "The Mega Combo",
    nameAr: "كومبو ميغا",
    description: "Any burger + fries + drink. Save $4!",
    descriptionAr: "أي برغر + بطاطس + مشروب. وفّر 4 دولار!",
    price: 14.99,
    image: "https://placehold.co/400x300/2A9D8F/fff?text=Mega+Combo",
    category: "combos",
    tags: [],
    popular: true,
    extras: [
      { name: "Upgrade to Large Fries", nameAr: "ترقية بطاطس كبيرة", price: 1 },
      { name: "Upgrade to Large Drink", nameAr: "ترقية مشروب كبير", price: 0.75 },
    ],
    removable: [],
  },
  {
    id: "7",
    name: "BBQ Bacon Burger",
    nameAr: "برغر باربكيو بيكون",
    description: "Beef patty, crispy bacon, BBQ sauce, onion rings",
    descriptionAr: "قطعة لحم بقري، لحم مقدد مقرمش، صوص باربكيو، حلقات بصل",
    price: 10.99,
    image: "https://placehold.co/400x300/C1121F/fff?text=BBQ+Burger",
    category: "burgers",
    tags: [],
    popular: false,
    sizes: [
      { label: "Single", priceAdd: 0 },
      { label: "Double", priceAdd: 2.5 },
    ],
    extras: [
      { name: "Extra Bacon", nameAr: "لحم مقدد إضافي", price: 1.5 },
      { name: "Onion Rings", nameAr: "حلقات بصل", price: 1 },
    ],
    removable: ["BBQ Sauce", "Onion Rings"],
  },
  {
    id: "8",
    name: "Crispy Chicken Tenders",
    nameAr: "قطع دجاج مقرمشة",
    description: "5 golden tenders with your choice of dip",
    descriptionAr: "5 قطع دجاج ذهبية مع صوص من اختيارك",
    price: 7.99,
    image: "https://placehold.co/400x300/E9C46A/333?text=Tenders",
    category: "chicken",
    tags: ["halal"],
    popular: false,
    sizes: [
      { label: "5pc", priceAdd: 0 },
      { label: "8pc", priceAdd: 3 },
    ],
    extras: [
      { name: "Ranch Dip", nameAr: "صوص رانش", price: 0.5 },
      { name: "Hot Sauce", nameAr: "صوص حار", price: 0.5 },
    ],
    removable: [],
  },
  {
    id: "9",
    name: "Onion Rings",
    nameAr: "حلقات بصل",
    description: "Beer-battered crispy onion rings",
    descriptionAr: "حلقات بصل مقرمشة بالعجينة",
    price: 4.49,
    image: "https://placehold.co/400x300/E76F51/fff?text=Onion+Rings",
    category: "sides",
    tags: ["vegan"],
    popular: false,
    extras: [
      { name: "Dipping Sauce", nameAr: "صوص تغميس", price: 0.5 },
    ],
    removable: [],
  },
  {
    id: "10",
    name: "Fresh Lemonade",
    nameAr: "عصير ليمون طازج",
    description: "Freshly squeezed with a hint of mint",
    descriptionAr: "عصير ليمون طازج مع نكهة النعناع",
    price: 3.49,
    image: "https://placehold.co/400x300/A8DADC/333?text=Lemonade",
    category: "drinks",
    tags: ["vegan"],
    popular: false,
    sizes: [
      { label: "Regular", priceAdd: 0 },
      { label: "Large", priceAdd: 1 },
    ],
    extras: [],
    removable: [],
  },
];

export const promotions: Promotion[] = [
  {
    id: "p1",
    title: "Buy 1 Get 1 Free on Burgers",
    titleAr: "اشترِ 1 واحصل على 1 مجاناً على البرغر",
    description: "Valid on all burgers, this week only!",
    descriptionAr: "صالح على جميع البرغر، هذا الأسبوع فقط!",
    code: "BOGO",
    discount: 50,
    image: "https://placehold.co/600x300/FF6B35/fff?text=BOGO+Burgers",
  },
  {
    id: "p2",
    title: "20% Off Combo Meals",
    titleAr: "خصم 20% على وجبات الكومبو",
    description: "Use code COMBO20 at checkout",
    descriptionAr: "استخدم الرمز COMBO20 عند الدفع",
    code: "COMBO20",
    discount: 20,
    image: "https://placehold.co/600x300/2A9D8F/fff?text=20%25+Off+Combos",
  },
  {
    id: "p3",
    title: "Free Delivery on Orders $25+",
    titleAr: "توصيل مجاني للطلبات فوق 25 دولار",
    description: "No minimum order, limited time",
    descriptionAr: "لا حد أدنى للطلب، لفترة محدودة",
    code: "FREEDEL",
    discount: 0,
    image: "https://placehold.co/600x300/E9C46A/333?text=Free+Delivery",
  },
];

export const sampleOrders: Order[] = [
  {
    id: "o1",
    number: "#1042",
    type: "dine-in",
    items: [
      { menuItemId: "1", name: "Classic Smash Burger", quantity: 2, price: 8.99 },
      { menuItemId: "3", name: "Loaded Fries", quantity: 1, price: 5.99 },
      { menuItemId: "4", name: "Chocolate Milkshake", quantity: 2, price: 4.99 },
    ],
    total: 28.96,
    status: "preparing",
    date: "2026-03-04T12:30:00",
    paymentMethod: "Card",
  },
  {
    id: "o2",
    number: "#1041",
    type: "delivery",
    items: [
      { menuItemId: "2", name: "Spicy Chicken Sandwich", quantity: 1, price: 9.49 },
      { menuItemId: "5", name: "Churros (6pc)", quantity: 1, price: 3.99 },
    ],
    total: 13.48,
    status: "out-for-delivery",
    date: "2026-03-04T11:45:00",
    paymentMethod: "Cash",
  },
  {
    id: "o3",
    number: "#1040",
    type: "takeaway",
    items: [
      { menuItemId: "6", name: "The Mega Combo", quantity: 1, price: 14.99 },
    ],
    total: 14.99,
    status: "completed",
    date: "2026-03-04T10:15:00",
    paymentMethod: "Card",
  },
];

export const notifications = [
  { id: "n1", message: "Order #1042 is being prepared!", messageAr: "جاري تحضير الطلب #1042!", time: "2 min ago", timeAr: "قبل دقيقتين", read: false },
  { id: "n2", message: "Order #1041 is out for delivery", messageAr: "الطلب #1041 في الطريق إليك", time: "15 min ago", timeAr: "قبل 15 دقيقة", read: false },
  { id: "n3", message: "Order #1040 is ready for pickup!", messageAr: "الطلب #1040 جاهز للاستلام!", time: "45 min ago", timeAr: "قبل 45 دقيقة", read: true },
];
